import React from 'react'
import { AnnotationTool, ANNOTATION_COLORS, ANNOTATION_SIZES, TOOL_CONFIG } from './types'

interface AnnotationToolbarProps {
  currentTool: AnnotationTool
  toolColor: string
  toolSize: number
  onToolChange: (tool: AnnotationTool) => void
  onColorChange: (color: string) => void
  onSizeChange: (size: number) => void
  onUndo: () => void
  onRedo: () => void
  onClear: () => void
  onScreenshot: () => void
  canUndo: boolean
  canRedo: boolean
  className?: string
}

export const AnnotationToolbar: React.FC<AnnotationToolbarProps> = ({
  currentTool,
  toolColor,
  toolSize,
  onToolChange,
  onColorChange,
  onSizeChange,
  onUndo,
  onRedo,
  onClear,
  onScreenshot,
  canUndo,
  canRedo,
  className = ''
}) => {
  return (
    <div className={`bg-white border border-gray-200 rounded-lg shadow-lg p-4 ${className}`}>
      {/* Tools Row */}
      <div className="mb-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Tools</h3>
        <div className="flex flex-wrap gap-2">
          {(Object.keys(TOOL_CONFIG) as AnnotationTool[]).map(tool => (
            <button
              key={tool}
              onClick={() => onToolChange(tool)}
              className={`
                px-3 py-2 rounded-md text-sm font-medium transition-colors
                ${currentTool === tool 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
              title={TOOL_CONFIG[tool].description}
            >
              <span className="mr-1">{TOOL_CONFIG[tool].icon}</span>
              {TOOL_CONFIG[tool].name}
            </button>
          ))}
        </div>
      </div>

      {/* Colors Row */}
      <div className="mb-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Colors</h3>
        <div className="flex flex-wrap gap-2">
          {ANNOTATION_COLORS.map(color => (
            <button
              key={color}
              onClick={() => onColorChange(color)}
              className={`
                w-8 h-8 rounded-md border-2 transition-transform hover:scale-110
                ${toolColor === color ? 'border-gray-800' : 'border-gray-300'}
              `}
              style={{ backgroundColor: color }}
              title={`Select ${color} color`}
            >
              {toolColor === color && (
                <div className="w-full h-full flex items-center justify-center text-white">
                  ✓
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Size Row */}
      <div className="mb-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">
          Size: {ANNOTATION_SIZES.find(s => s.value === toolSize)?.label}
        </h3>
        <div className="flex gap-2">
          {ANNOTATION_SIZES.map(size => (
            <button
              key={size.value}
              onClick={() => onSizeChange(size.value)}
              className={`
                px-3 py-2 rounded-md text-sm font-medium transition-colors
                ${toolSize === size.value
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
            >
              <div 
                className="bg-current rounded-full mx-auto"
                style={{ 
                  width: Math.max(size.value * 2, 8), 
                  height: Math.max(size.value * 2, 8) 
                }}
              />
            </button>
          ))}
        </div>
      </div>

      {/* Actions Row */}
      <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
        <button
          onClick={onScreenshot}
          className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 text-sm font-medium"
        >
          📷 Screenshot
        </button>
        
        <button
          onClick={onUndo}
          disabled={!canUndo}
          className={`
            px-4 py-2 rounded-md text-sm font-medium
            ${canUndo 
              ? 'bg-gray-500 text-white hover:bg-gray-600' 
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          ↶ Undo
        </button>
        
        <button
          onClick={onRedo}
          disabled={!canRedo}
          className={`
            px-4 py-2 rounded-md text-sm font-medium
            ${canRedo 
              ? 'bg-gray-500 text-white hover:bg-gray-600' 
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          ↷ Redo
        </button>
        
        <button
          onClick={onClear}
          className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm font-medium"
        >
          🗑️ Clear All
        </button>
      </div>

      {/* Quick Instructions */}
      <div className="mt-4 p-3 bg-blue-50 rounded-md text-sm text-blue-800">
        <div className="font-medium mb-1">Quick Tips:</div>
        <ul className="text-xs space-y-1">
          <li>• Use Select tool to move or delete annotations</li>
          <li>• Click selected annotation's delete button to remove it</li>
          <li>• Take screenshots to capture current view with annotations</li>
        </ul>
      </div>
    </div>
  )
}